import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Navigation, MapPin, Filter } from 'lucide-react';
import { api } from '../../services/api.js';
import { ALL_STORE_TYPES, STORE_TYPE_CATEGORIES } from './StoreTypeSelector.js';
import { berlinTodayIso } from '../../utils/datetime';

interface ScoredPoS {
  pos_id: string;
  pos_name: string;
  address: string;
  latitude: number;
  longitude: number;
  score: number;
  breakdown: {
    followup_score: number;
    time_score: number;
    proximity_score: number;
    value_score: number;
  };
  needs_followup: boolean;
  followup_priority?: string;
  days_since_visit: number;
  distance_km: string;
}

interface PlanVisitsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PlanVisitsModal: React.FC<PlanVisitsModalProps> = ({ isOpen, onClose }) => {
  const [count, setCount] = useState(10);
  const [startOption, setStartOption] = useState<'office' | 'custom'>('office');
  const [customAddress, setCustomAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<ScoredPoS[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startingPoint, setStartingPoint] = useState<{latitude: number; longitude: number} | null>(null);
  const [batchName, setBatchName] = useState('');
  const [plannedDate, setPlannedDate] = useState(berlinTodayIso());
  const [selectedStoreTypes, setSelectedStoreTypes] = useState<string[]>([]);
  const [showStoreFilter, setShowStoreFilter] = useState(true);
  
  const handlePlan = async () => {
    setLoading(true);
    setError(null);
    try {
      let coordinates;
      
      if (startOption === 'office') {
        // Fixed office location (Leipziger Straße 57, 10117 Berlin)
        coordinates = { latitude: 52.5118, longitude: 13.3883 };
      } else {
        try {
          const geocodeRes = await api.get(
            `/geocode?address=${encodeURIComponent(customAddress.trim())}`
          );
          coordinates = {
            latitude: geocodeRes.data.latitude,
            longitude: geocodeRes.data.longitude,
          };
        } catch (geoErr: any) {
          const msg =
            geoErr.response?.status === 404
              ? 'Address not found. Try a more specific address.'
              : 'Failed to geocode address. Please try again.';
          toast.error(msg);
          setLoading(false);
          return;
        }
      }
      
      const res = await api.post('/visits/plan', {
        count,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        ...(selectedStoreTypes.length > 0 && {
          store_types: selectedStoreTypes,
        }),
      });
      
      setStartingPoint(coordinates);
      setPlan(res.data);
    } catch (err: any) {
      console.error('Failed to plan visits', err);
      setError(err.message || 'Failed to generate visit plan');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoute = async () => {
    if (!plan || !startingPoint) {
      toast.warning('No route to save');
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        name: batchName || `Route - ${new Date().toLocaleDateString()}`,
        description: `${plan.length} PoS visits planned`,
        planned_date: plannedDate,
        starting_point: startingPoint,
        pos_items: plan.map((pos, index) => ({
          pos_id: pos.pos_id,
          sequence_order: index + 1,
          total_score: pos.score,
          followup_score: pos.breakdown.followup_score,
          time_score: pos.breakdown.time_score,
          proximity_score: pos.breakdown.proximity_score,
          value_score: pos.breakdown.value_score,
          distance_from_previous_km: index === 0 ? 0 : parseFloat(pos.distance_km),
        })),
      };

      await api.post('/visit-batches', payload);
      toast.success('Route saved successfully!');
      onClose();
      window.location.href = '/routes'; // Navigate to routes page
    } catch (err) {
      console.error('Failed to create route:', err);
      toast.error('Failed to save route');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <Navigation className="text-purple-600" size={28} />
            <h2 className="text-2xl font-black text-gray-900">Plan Visits</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        {!plan ? (
          <div className="p-6 space-y-6">
            <p className="text-gray-600">
              Generate an intelligent visit route based on follow-up priorities, time since last visit, proximity, and store value.
            </p>

            {/* Number of visits */}
            <div>
              <label className="block font-bold text-gray-900 mb-2">How many visits do you want to plan?</label>
              <input
                type="number"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                min={1}
                max={50}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum 50 visits per plan</p>
            </div>
            
            {/* Store type filter */}
            <div>
              <button
                type="button"
                onClick={() => setShowStoreFilter(!showStoreFilter)}
                className="flex items-center gap-2 font-bold text-gray-900 mb-2"
              >
                <Filter size={18} className="text-purple-600" />
                <span>Filter by store type</span>
                {selectedStoreTypes.length > 0 && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                    {selectedStoreTypes.length} selected
                  </span>
                )}
                <span className="text-gray-400 text-sm font-normal ml-auto">
                  {showStoreFilter ? 'Hide' : 'Show'}
                </span>
              </button>

              {showStoreFilter && (
                <div className="border-2 border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setSelectedStoreTypes(ALL_STORE_TYPES.map((t) => t.value))}
                      className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-bold hover:bg-purple-200 transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedStoreTypes([])}
                      className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded-full font-bold hover:bg-gray-200 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                  {STORE_TYPE_CATEGORIES.map((category) => (
                    <div key={category.category}>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{category.category}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                        {category.items.map((type) => {
                          const isSelected = selectedStoreTypes.includes(type.value);
                          return (
                            <label
                              key={type.value}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border-2 transition-all text-sm ${
                                isSelected
                                  ? 'border-purple-400 bg-purple-50 text-purple-900'
                                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  setSelectedStoreTypes((prev) =>
                                    isSelected
                                      ? prev.filter((v) => v !== type.value)
                                      : [...prev, type.value]
                                  );
                                }}
                                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                              />
                              <span className="font-medium">{type.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">
                    {selectedStoreTypes.length === 0
                      ? 'No filter active -- all store types will be included.'
                      : `Only ${selectedStoreTypes.length} store type(s) will be included.`}
                  </p>
                </div>
              )}
            </div>

            {/* Starting point */}
            <div>
              <label className="block font-bold text-gray-900 mb-3">Starting point</label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-all">
                  <input
                    type="radio"
                    checked={startOption === 'office'}
                    onChange={() => setStartOption('office')}
                    className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-bold text-gray-900">Office location (Leipziger Straße 57, 10117 Berlin)</div>
                    <div className="text-sm text-gray-600">Start from the default office address</div>
                  </div>
                  <MapPin size={20} className="text-gray-400" />
                </label>
                
                <label className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-all">
                  <input
                    type="radio"
                    checked={startOption === 'custom'}
                    onChange={() => setStartOption('custom')}
                    className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-bold text-gray-900">Custom address</div>
                    <div className="text-sm text-gray-600">Enter a specific starting location</div>
                  </div>
                </label>
                
                {startOption === 'custom' && (
                  <input
                    type="text"
                    value={customAddress}
                    onChange={(e) => setCustomAddress(e.target.value)}
                    placeholder="Enter address (e.g., Warschauer Straße 70, Berlin)..."
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg ml-0 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                )}
              </div>
            </div>
            
            {error && (
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg text-red-700">
                <strong>Error:</strong> {error}
              </div>
            )}
            
            <button
              onClick={handlePlan}
              disabled={loading || (startOption === 'custom' && !customAddress)}
              className="w-full py-4 bg-purple-600 text-white font-black rounded-xl text-lg hover:bg-purple-700 transition-all shadow-lg shadow-purple-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider"
            >
              {loading ? 'Planning Route...' : 'Generate Visit Plan'}
            </button>
          </div>
        ) : (
          <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-black text-xl text-gray-900">Optimized Route ({plan.length} visits)</h3>
              <div className="text-sm text-gray-600">
                Total Distance: <span className="font-bold">{plan.reduce((sum, p) => sum + parseFloat(p.distance_km), 0).toFixed(1)} km</span>
              </div>
            </div>
            
            {/* Visit list with scores */}
            <div className="space-y-3 mb-6 max-h-[500px] overflow-y-auto">
              {plan.map((pos, index) => (
                <div key={pos.pos_id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-purple-400 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3 flex-1">
                      <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-black text-lg flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-black text-gray-900 text-lg">{pos.pos_name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{pos.address}</p>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          <span>📍 {pos.distance_km} km away</span>
                          <span>🕒 {pos.days_since_visit} days since visit</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right flex-shrink-0 ml-4">
                      {pos.needs_followup && (
                        <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                          pos.followup_priority === 'urgent'
                            ? 'bg-fuchsia-100 text-fuchsia-800'
                            : pos.followup_priority === 'high' 
                            ? 'bg-red-100 text-red-700' 
                            : pos.followup_priority === 'medium'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          FOLLOW-UP: {pos.followup_priority?.toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Save Route Form */}
            <div className="mt-6 border-t-2 border-gray-200 pt-6">
              <h3 className="font-bold text-gray-900 mb-4">Save This Route</h3>
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Route Name</label>
                  <input
                    type="text"
                    placeholder={`Route - ${new Date().toLocaleDateString()}`}
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Planned Date</label>
                  <input
                    type="date"
                    value={plannedDate}
                    onChange={(e) => setPlannedDate(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setPlan(null)}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-300 transition-colors"
                >
                  Plan Again
                </button>
                <button
                  onClick={handleCreateRoute}
                  disabled={loading}
                  className="flex-1 py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-colors shadow-lg shadow-green-200 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Create Route'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

